import { ReactNode, useContext, useMemo } from 'react';
import { AppContext } from 'context/appContext';
import { Outlet, useOutletContext, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import FormTabNavigation, {
  FormTab,
} from 'components/layout/FormTabNavigation';
import { ROUTES, routeWithParams } from 'router/routes';
import { Box } from '@mui/material';
import FormHeaderBar from 'components/layout/FormHeaderBar';
import { DisplayMode, PanelRootContext } from 'types/PanelRootContext';
import { Tenant, Property, PropertyGroup } from 'types';

const RulesFormLayout = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const { pathname } = useLocation();

  const { formMode, setFormMode, displayMode, entity, setEntity } =
    useOutletContext<PanelRootContext<Tenant | Property | PropertyGroup>>();

  const getRouteForDisplayMode = (
    tab: 'rules' | 'rulesets' | 'violations' | 'instructions',
    displayMode: DisplayMode,
  ) => {
    switch (`${tab}-${displayMode}`) {
      case 'rules-tenant':
        return routeWithParams(ROUTES.RULES, [state.tenant.id]);
      case 'rulesets-tenant':
        return routeWithParams(ROUTES.RULESETS, [state.tenant.id]);
      case 'violations-tenant':
        return routeWithParams(ROUTES.VIOLATIONS, [state.tenant.id]);
      case 'rules-property':
        return routeWithParams(ROUTES.PROPERTY_RULES, [
          state.tenant.id,
          entity.id,
        ]);
      case 'rulesets-property':
        return routeWithParams(ROUTES.PROPERTY_RULESETS, [
          state.tenant.id,
          entity.id,
        ]);
      case 'instructions-property':
        return routeWithParams(ROUTES.PROPERTY_INSTRUCTIONS, [
          state.tenant.id,
          entity.id,
        ]);
      case 'rules-group':
        return routeWithParams(ROUTES.PROPERTY_GROUP_RULES, [
          state.tenant.id,
          entity.id,
        ]);
      case 'rulesets-group':
        return routeWithParams(ROUTES.PROPERTY_GROUP_RULESETS, [
          state.tenant.id,
          entity.id,
        ]);
      default:
        return pathname;
    }
  };

  const tabs: FormTab[] = useMemo(() => {
    const tabs: FormTab[] = [];
    if (displayMode === 'tenant') {
      tabs.push({
        label: `${t('violation.label_plural', { ns: 'entities' })}`,
        route: getRouteForDisplayMode('violations', displayMode),
        beforeNavigate: () => {
          setFormMode('none');
          return true;
        },
      });
    }
    tabs.push({
      label: `${t('rule.label_plural', { ns: 'entities' })}`,
      route: getRouteForDisplayMode('rules', displayMode),
      beforeNavigate: () => {
        setFormMode('none');
        return true;
      },
    });
    tabs.push({
      label: `${t('ruleset.label_plural', { ns: 'entities' })}`,
      route: getRouteForDisplayMode('rulesets', displayMode),
      beforeNavigate: () => {
        setFormMode('none');
        return true;
      },
    });

    if (displayMode === 'property') {
      tabs.push({
        label: `${t('instruction.label_plural', { ns: 'entities' })}`,
        route: getRouteForDisplayMode('instructions', displayMode),
        beforeNavigate: () => {
          setFormMode('none');
          return true;
        },
      });
    }

    return tabs;
  }, [displayMode]);

  return (
    <Box
      flexGrow={1}
      display={'flex'}
      flexDirection={'column'}
      minHeight={0}
      sx={{
        overflow: 'hidden',
      }}
    >
      <FormHeaderBar />
      {formMode === 'none' && (
        <FormTabNavigation tabs={tabs} title={t('title', { ns: 'rules' })} />
      )}
      <Box
        flexGrow={1}
        minHeight={0}
        display={'flex'}
        flexDirection={'column'}
        sx={{
          overflow: 'hidden',
        }}
      >
        <Outlet
          context={
            {
              formMode,
              setFormMode,
              entity,
              setEntity,
              displayMode,
            } satisfies PanelRootContext<Tenant | Property | PropertyGroup>
          }
        />
      </Box>
    </Box>
  );
};

RulesFormLayout.displayName = 'RulesFormLayout';
export default RulesFormLayout;
